import React, { createContext, useMemo, useContext, useState, useEffect, useCallback } from 'react';

import createFilters from '../../filterFactory/createFilters';
import { FilterTypes } from '../../../../../modules/sortAndFilter/workers/sortAndFilter';

export interface FilterValues {
    filters: ReturnType<typeof createFilters>;
    numberOfFiltersApplied: number;
    lastFilterUpdated: FilterTypes['type'] | null;
}

interface UpdaterFunctions {
    setFilters: React.Dispatch<React.SetStateAction<ReturnType<typeof createFilters>>>;
    setLastFilterUpdated: React.Dispatch<React.SetStateAction<FilterTypes['type'] | null>>;
}

interface Props {
    children: React.ReactNode;
    urlKey: string;
    onFilterChange?: () => void;
}

export const TreatmentFilterStateContext = createContext({});
export const TreatmentFilterUpdaterContext = createContext({});

/**
 * React context that provides the sort and filter controls for the treatment list. A page config can be
 * loaded in. A global config is always loaded in. Treatments must be passed in so that they can be
 * filtered against.
 */
const TreatmentFilterProvider = ({ urlKey, children, onFilterChange }: Props) => {
    const [filters, setFiltersState] = useState(() => createFilters(urlKey));
    const [lastFilterUpdated, setLastFilterUpdated] = useState<FilterTypes['type'] | null>(null);

    // Abstracted setFilters so that we can pop an 'onFilterChange' in it.
    const setFilters = useCallback((state: any) => {
        if (onFilterChange) onFilterChange();
        setFiltersState(state);
    }, [onFilterChange]);

    useEffect(() => {
        filters.forEach((filter) => filter.addKeysToUrl());
    }, [filters]);

    const numberOfFiltersApplied = useMemo(() => {
        const filterValues = Array.from(filters.values());
        return filterValues.reduce((acc, filter) => acc + filter.keys.size, 0);
    }, [filters]);

    useEffect(() => {
        if (!numberOfFiltersApplied) {
            setLastFilterUpdated(null);
        }
    }, [numberOfFiltersApplied]);

    const value = useMemo(
        () => ({
            filters,
            numberOfFiltersApplied,
            lastFilterUpdated,
        }),
        [filters, numberOfFiltersApplied, lastFilterUpdated]
    );

    const updaterFunctions = useMemo(
        () => ({
            setLastFilterUpdated,
            setFilters,
        }),
        []
    );

    return (
        <TreatmentFilterStateContext.Provider value={value}>
            <TreatmentFilterUpdaterContext.Provider value={updaterFunctions}>{children}</TreatmentFilterUpdaterContext.Provider>
        </TreatmentFilterStateContext.Provider>
    );
};

export const useTreatmentFilterStateContext = () => {
    const filterValues = useContext(TreatmentFilterStateContext) as FilterValues;

    if (filterValues === undefined) {
        throw new Error('useTreatmentFilterStateContext must be used within a TreatmentFilterProvider.');
    }

    return filterValues;
};

export const useTreatmentFilterUpdaterContext = () => {
    const updaterFunctions = useContext(TreatmentFilterUpdaterContext) as UpdaterFunctions;

    if (updaterFunctions === undefined) {
        throw new Error('useTreatmentFilterUpdaterContext must be used within a TreatmentFilterProvider.');
    }

    const { setFilters, setLastFilterUpdated } = updaterFunctions;

    const updateFilters = () => setFilters((state) => new Map(state));
    const clearAllFilters = () => {
        setFilters((state) => {
            const newFilters = new Map(state);
            newFilters.forEach((filter) => filter.keys.clear());
            return newFilters;
        });
    };

    return {
        updateFilters,
        clearAllFilters,
        setLastFilterUpdated,
    };
};

export default TreatmentFilterProvider;
